import { Injectable } from '@angular/core';
import moment from 'moment';

import { LanguageService } from '../LanguageService';

const words: { [unit: string]: { [lang: string]: string} } = {
  years: { en_us: 'Years', es_mx: 'Años'},
  months: { en_us: 'Months', es_mx: 'Meses'},
  days: { en_us: 'Days', es_mx: 'Días'},
  weeks: { en_us: 'Weeks', es_mx: 'Semanas'},
  deceased: { en_us: 'deceased at', es_mx: 'fallecido en'},
};

export interface PersonAgeInfo {
  birthdate: string;
  deathdate?: string;
  age: number;
  units: string;
  deceased?: boolean;
}

export interface DisplayPersonAgeOptions {
  display?: 'age' | 'birthdate';
}

@Injectable({
  providedIn: 'root',
})
export class DateUtilities {
  static readonly ajsFactoryName = 'DateUtilities';

  constructor(
    private readonly languageService: LanguageService,
  ) { }

  private getLangCode() {
    return this.languageService.getLang().code.toLowerCase();
  }

  getNowDate() {
    return new Date();
  }

  getNowMoment() {
    return moment();
  }

  getPersonAgeAbbr(birthDate: string, deathDate?: string | null, unit?: 'days' | 'weeks' | 'months' | 'years'): string {
    if (!moment(birthDate, 'MM/DD/YYYY').isValid()) {
      birthDate = moment(birthDate).format('L');
    }
    const ageInfo = this.getPersonAge(birthDate, deathDate, unit);
    const age = ageInfo.age + '' + ageInfo.units.charAt(0).toLowerCase();
    return age;
  }

  // we need to assume the dates from the server are in mm/dd/yyyy format
  // but we can use momentjs to format based on the language selection
  // Framework\Util\PrmDateTime.cs
  getPersonAge(birthDate: string, deathDate?: string | null, unit?: 'days' | 'weeks' | 'months' | 'years'): PersonAgeInfo {
    const langCode = this.getLangCode();
    moment.locale(langCode);

    const birthDateMoment = moment(birthDate + '', 'MM/DD/YYYY');
    if (!birthDateMoment.isValid()) {
      return {
        birthdate: '',
        age: 0,
        units: words.years[langCode],
      };
    }

    const tempDeathDate = moment(deathDate + '', 'MM/DD/YYYY');
    let _deceased = false;
    let end = tempDeathDate;
    if (tempDeathDate.isValid()) {
      _deceased = true;
    } else {
      end = this.getNowMoment();
    }

    const years = end.diff(birthDateMoment, 'years');
    const months = end.diff(birthDateMoment, 'months');
    const days = end.diff(birthDateMoment, 'days');
    const weeks = end.diff(birthDateMoment, 'weeks');

    let _units = 'Years';
    let _age = 0;
    if ((!unit && days < 14) || unit === 'days') {
      _age = days;
      _units = words.days[langCode] || 'Days';
    } else if ((!unit && months < 2) || unit === 'weeks') {
      _age = weeks;
      _units = words.weeks[langCode] || 'Weeks';
    } else if ((!unit && years < 2) || unit === 'months') {
      _age = months;
      _units = words.months[langCode] || 'Months';
    } else {
      _age = years;
      _units = words.years[langCode] || 'Years';
    }

    return {
      birthdate: birthDateMoment.format('L'),
      deathdate: tempDeathDate.format('L'),
      age: _age,
      units: _units,
      deceased: _deceased,
    };
  }

  displayAge(person: PersonAgeInfo, options?: DisplayPersonAgeOptions): string {
    const langCode = this.getLangCode();

    if (!person || person.birthdate === '') {
      return '';
    }

    let ageString = person.age + ' ' + person.units;

    if (person.deceased) {
      ageString = words.deceased[langCode] + ' ' + ageString;
    }

    if (options && options.display === 'age') {
      return ageString;
    }

    if (options && options.display === 'birthdate') {
      return person.birthdate;
    }
    const outputString = person.birthdate + ' (' + ageString + ')';
    return outputString;
  }

  getDurationString(refDate: string): string {
    const diff = moment(refDate).diff(this.getNowMoment());
    const msg = moment.duration(diff).humanize();
    if (diff < 0) {
      return msg + ' ago';
    }
    else {
      return 'in ' + msg;
    }
  }
}
